import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { useTheme } from "../contexts/ThemeContext";
import { decodePolyline } from "../utils/decodePolyline";

const alertColors = {
  security: "#EF4444",
  fire: "#F97316",
  medical: "#3B82F6",
  accident: "#EAB308",
  banditry: "#881337",
  ecaution: "#D97706",
  location_share: "#059669",
};

const alertLabels = {
  security: "Security Threat",
  fire: "Fire",
  medical: "Medical Emergency",
  accident: "Accident",
  banditry: "Banditry Attack",
  ecaution: "Road Caution",
  location_share: "Location Share",
};

const RADIUS_PATH_OPTIONS = {
  color: "#EF4444",
  fillColor: "rgba(239, 68, 68, 0.15)",
  fillOpacity: 0.15,
  weight: 2,
  dashArray: "8 4",
};

function createMarkerIcon(type) {
  const color = alertColors[type] || "#6B7280";
  return L.divIcon({
    className: "",
    html: `
      <div style="position:relative;width:28px;height:28px;">
        <div style="width:28px;height:28px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);position:relative;z-index:2;"></div>
        <div style="width:28px;height:28px;background:${color};border-radius:50%;position:absolute;top:0;left:0;animation:marker-ping 1.5s cubic-bezier(0,0,0.2,1) infinite;opacity:0.4;z-index:1;"></div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

// Walking-figure pin used for live-tracked users. Distinct from alert pins
// (square/cyan + arrow) so watchers can spot the moving citizen at a glance.
const trackedUserIcon = L.divIcon({
  className: "",
  html: `
    <div style="position:relative;width:30px;height:30px;">
      <div style="width:30px;height:30px;background:#0EA5E9;border:3px solid white;border-radius:6px;box-shadow:0 2px 8px rgba(14,165,233,0.6);display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-size:13px;font-family:system-ui;position:relative;z-index:2;">↗</div>
      <div style="width:30px;height:30px;background:#0EA5E9;border-radius:6px;position:absolute;top:0;left:0;animation:marker-ping 1.5s cubic-bezier(0,0,0.2,1) infinite;opacity:0.5;z-index:1;"></div>
    </div>
  `,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

const TRACK_PATH_OPTIONS = {
  color: "#0EA5E9",
  weight: 4,
  opacity: 0.9,
  dashArray: "0",
  lineCap: "round",
  lineJoin: "round",
};

const userIcon = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;background:#111827;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function isValidCoord(loc) {
  return loc && typeof loc.latitude === "number" && typeof loc.longitude === "number" && !isNaN(loc.latitude) && !isNaN(loc.longitude);
}

function FlyToLocation({ location }) {
  const map = useMap();
  useEffect(() => {
    if (!isValidCoord(location)) return;
    // Skip if map container is hidden (zero dimensions) — prevents NaN in flyTo animation
    const el = map.getContainer();
    if (!el || el.clientWidth === 0 || el.clientHeight === 0) return;
    map.setView([location.latitude, location.longitude], 13);
  }, [location, map]);
  return null;
}

function formatTime(isoStr) {
  const d = new Date(isoStr);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function AlertMarker({ alert, isFocused, focusToken, onPopupOpen }) {
  const icon = useMemo(() => createMarkerIcon(alert.alert_type), [alert.alert_type]);
  const position = useMemo(() => [alert.latitude, alert.longitude], [alert.latitude, alert.longitude]);
  const markerRef = useRef(null);

  // When the parent focuses this alert, auto-open the popup. We key the
  // effect off `focusToken` (a per-click reference from Dashboard) so even
  // repeated clicks of the same alert re-open the popup cleanly. A small
  // setTimeout(0) gives the FlyToLocation effect time to settle so the
  // popup opens at the correct on-screen position.
  useEffect(() => {
    if (!isFocused) return;
    const m = markerRef.current;
    if (!m || typeof m.openPopup !== "function") return;
    const t = setTimeout(() => { try { m.openPopup(); } catch { /* marker may have unmounted mid-flight */ } }, 100);
    return () => clearTimeout(t);
  }, [isFocused, focusToken]);

  // When a user clicks the marker directly (NOT via the feed), tell the
  // parent so the matching feed card can highlight + scroll into view.
  // We don't touch `focusLocation` from this path — only `focusAlertId` —
  // so we avoid an infinite open→focus→open loop.
  const eventHandlers = useMemo(() => ({
    popupopen: () => { onPopupOpen?.(alert.alert_id); },
  }), [onPopupOpen, alert.alert_id]);

  const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${alert.latitude},${alert.longitude}`;

  return (
    <Marker position={position} icon={icon} ref={markerRef} eventHandlers={eventHandlers}>
      <Popup>
        <div className="font-body min-w-[200px]">
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: alertColors[alert.alert_type] }}>
            {alertLabels[alert.alert_type]}
          </p>
          <p className="text-sm text-gray-800 mb-1">{alert.description}</p>
          <p className="text-xs text-gray-400 mb-2">
            {alert.user_name} &middot; {formatTime(alert.created_at)}
          </p>
          <a
            href={navUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`navigate-to-${alert.alert_id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-black text-white text-[11px] font-bold rounded-sm hover:bg-gray-800 transition-colors"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Navigate
          </a>
        </div>
      </Popup>
    </Marker>
  );
}

const radiusColors = {
  security: { color: "#EF4444", fill: "rgba(239, 68, 68, 0.12)" },
  fire: { color: "#F97316", fill: "rgba(249, 115, 22, 0.10)" },
  medical: { color: "#3B82F6", fill: "rgba(59, 130, 246, 0.10)" },
  accident: { color: "#EAB308", fill: "rgba(234, 179, 8, 0.10)" },
  banditry: { color: "#881337", fill: "rgba(136, 19, 55, 0.14)" },
  ecaution: { color: "#D97706", fill: "rgba(217, 119, 6, 0.14)" },
  location_share: { color: "#059669", fill: "rgba(5, 150, 105, 0.08)" },
};

function AlertRadius({ alert, radius }) {
  const center = useMemo(() => [alert.latitude, alert.longitude], [alert.latitude, alert.longitude]);
  const colors = radiusColors[alert.alert_type] || radiusColors.security;
  const pathOptions = useMemo(() => ({
    color: colors.color,
    fillColor: colors.fill,
    fillOpacity: 0.15,
    weight: 2,
    dashArray: "8 4",
  }), [colors]);
  return <Circle center={center} radius={radius * 1000} pathOptions={pathOptions} />;
}

function HeatmapLayer({ alerts, visible }) {
  const map = useMap();

  useEffect(() => {
    if (!visible || !alerts.length) return;

    const points = alerts.map((a) => [a.latitude, a.longitude, 0.6]);
    const heat = L.heatLayer(points, {
      radius: 30,
      blur: 20,
      maxZoom: 15,
      gradient: { 0.2: "#3B82F6", 0.4: "#EAB308", 0.6: "#F97316", 0.8: "#EF4444", 1: "#991B1B" },
    }).addTo(map);

    return () => { map.removeLayer(heat); };
  }, [alerts, visible, map]);

  return null;
}

function MapClickHandler({ onLocationSet, hasLocation }) {
  const handleClick = useCallback((e) => {
    if (onLocationSet) {
      onLocationSet({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    }
  }, [onLocationSet]);

  useMapEvents({
    click: handleClick,
  });

  return null;
}

export default function MapView({ alerts = [], userLocation, proximityRadius = {}, onLocationSet, focusLocation, focusAlertId, onMarkerFocus, trackingSessions = {}, recommendedRoute = null }) {
  const { theme } = useTheme();
  // Google Maps tiles are now the only basemap, served free without an API
  // key from Google's `mt` raster endpoint (`lyrs=m` = road map with labels).
  // CARTO light/dark tiles are kept as a SILENT errorTileUrl fallback so the
  // map degrades gracefully if Google blocks an IP / rate-limits a session.
  const googleTileUrl = "https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}";
  const googleSubdomains = ["0", "1", "2", "3"];
  const fallbackTileUrl = theme === "dark"
    ? "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
    : "https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png";
  const [showHeatmap, setShowHeatmap] = useState(false);
  const validLocation = isValidCoord(userLocation);
  const validFocus = isValidCoord(focusLocation);

  const defaultCenter = useMemo(
    () => validFocus ? [focusLocation.latitude, focusLocation.longitude]
      : validLocation ? [userLocation.latitude, userLocation.longitude]
      : [6.5244, 3.3792],
    [validFocus, focusLocation, validLocation, userLocation]
  );

  const userPosition = useMemo(
    () => validLocation ? [userLocation.latitude, userLocation.longitude] : null,
    [validLocation, userLocation]
  );

  // Get radius for a given alert type
  const getRadius = useCallback((type) => {
    if (typeof proximityRadius === "object" && proximityRadius !== null) {
      return proximityRadius[type] || 10;
    }
    return typeof proximityRadius === "number" ? proximityRadius : 10;
  }, [proximityRadius]);

  // Decode the AI-recommended route once per polyline change.
  const recommendedPath = useMemo(() => {
    if (!recommendedRoute?.polyline) return null;
    const pts = decodePolyline(recommendedRoute.polyline);
    return pts.length > 1 ? pts : null;
  }, [recommendedRoute]);

  return (
    <div className="w-full h-full relative isolate z-0" data-testid="map-container">
      {!validLocation && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-black/80 text-white text-xs font-body px-3 py-1.5 rounded-sm pointer-events-none" data-testid="map-click-hint">
          Tap the map to set your location
        </div>
      )}
      {/* Heatmap toggle */}
      <button
        onClick={() => setShowHeatmap((p) => !p)}
        data-testid="heatmap-toggle"
        className={`absolute top-2 right-2 z-[1000] px-2.5 py-1.5 text-[11px] font-bold font-body rounded-sm border shadow-sm transition-colors duration-100 ${
          showHeatmap
            ? "bg-black text-white border-black"
            : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"
        }`}
      >
        {showHeatmap ? "Hide Heatmap" : "Heatmap"}
      </button>

      <MapContainer center={defaultCenter} zoom={12} className="w-full h-full" zoomControl={true}>
        <TileLayer
          attribution='&copy; Google Maps'
          url={googleTileUrl}
          subdomains={googleSubdomains}
          errorTileUrl={fallbackTileUrl}
          maxZoom={20}
        />

        <MapClickHandler onLocationSet={onLocationSet} hasLocation={validLocation} />
        <HeatmapLayer alerts={alerts} visible={showHeatmap} />

        {validLocation && <FlyToLocation location={userLocation} />}
        {validFocus && <FlyToLocation location={focusLocation} />}

        {userPosition && (
          <Marker position={userPosition} icon={userIcon}>
            <Popup><p className="font-body text-sm font-semibold">Your Location</p></Popup>
          </Marker>
        )}

        {alerts.map((alert) => (
          <AlertMarker key={alert.alert_id} alert={alert} isFocused={alert.alert_id === focusAlertId} focusToken={focusLocation} onPopupOpen={onMarkerFocus} />
        ))}

        {alerts.map((alert) => (
          <AlertRadius key={`radius-${alert.alert_id}`} alert={alert} radius={getRadius(alert.alert_type)} />
        ))}

        {/* Live tracking sessions — moving cyan pin + breadcrumb polyline. */}
        {Object.values(trackingSessions).map(({ session, points }) => {
          if (!points || points.length === 0) return null;
          const latest = points[points.length - 1];
          const trail = points
            .filter((p) => typeof p.latitude === "number" && typeof p.longitude === "number")
            .map((p) => [p.latitude, p.longitude]);
          return (
            <div key={session.session_id}>
              {trail.length > 1 && <Polyline positions={trail} pathOptions={TRACK_PATH_OPTIONS} />}
              <Marker position={[latest.latitude, latest.longitude]} icon={trackedUserIcon}>
                <Popup>
                  <div className="font-body text-sm space-y-1" data-testid={`track-popup-${session.session_id}`}>
                    <p className="font-bold text-cyan-700">Live tracking</p>
                    <p className="text-gray-700">
                      {session.user_name || "User"} sharing live location
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      {trail.length} point{trail.length === 1 ? "" : "s"}
                    </p>
                  </div>
                </Popup>
              </Marker>
            </div>
          );
        })}
        {/* AI-recommended route polyline (Driving Mode → Safe Route advisory). */}
        {recommendedPath && (
          <Polyline
            positions={recommendedPath}
            pathOptions={{
              color: "#10B981",
              weight: 5,
              opacity: 0.85,
              dashArray: "1 0",
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        )}
      </MapContainer>
    </div>
  );
}
